import { useRef, useState } from 'react'
import {
  AutoComplete,
  type AutoCompleteChangeEvent,
  type AutoCompleteCompleteEvent,
  type AutoCompletePassThroughOptions,
} from 'primereact/autocomplete'

/** En az bu kadar karakterde arama tetiklenir — tek harf neredeyse tüm dizini döndürürdü. */
export const USER_PICKER_MIN_CHARS = 2

/** Seçicinin tanıdığı en küçük kullanıcı biçimi; çağıran daha geniş bir tip verebilir. */
export interface PickableUser {
  id: number
  name: string | null
  surname: string | null
  employeeId?: string | null
}

interface Props {
  /**
   * İçteki arama kutusunun `id`'si — çağıran bununla **gerçek bir `<label htmlFor>`** bağlar.
   *
   * <p>`aria-label` ile adlandırmak yetmez: PrimeReact çoklu modda kutuyu bir `ul` içine gizli bir
   * `input` olarak çizer ve prop'u köke koyar, yani alan erişilebilir adını almaz (25.09.2026'da
   * ölçüldü — test alanı bulamadı). Görünür etiket ayrıca gözle de bir yarar sağlar.
   */
  inputId: string
  /** Seçili kullanıcılar (çip listesi). Kaynak **çağıranda** durur; bileşen kendi state'i tutmaz. */
  value: PickableUser[]
  onChange: (users: PickableUser[]) => void
  /**
   * Arama işlevi — **dışarıdan verilir**. Bileşen bir uca bağlanmaz: hangi kullanıcıların
   * aranacağı (ve kimlerin eleneceği) çağıranın bağlamına aittir; uç buraya gömülseydi ikinci bir
   * kullanım için bileşenin kopyalanması gerekirdi.
   */
  search: (q: string) => Promise<PickableUser[]>
  placeholder: string
  disabled?: boolean
  autoFocus?: boolean
  /**
   * Alan geçersiz durumda mı — içteki girdiye `aria-invalid` olarak yazılır.
   *
   * <p>`describedBy` ile birlikte kullanılır: hata metnini yalnız ekrana basmak, ekran okuyucu
   * kullanıcısına hiçbir şey söylemez (THY inceleme bulgusu, 25.09.2026).
   */
  invalid?: boolean
  /** İçteki girdinin `aria-describedby` listesi — hata/yardım metninin `id`'si. */
  describedBy?: string
}

function fullName(user: PickableUser): string {
  return `${user.name ?? ''} ${user.surname ?? ''}`.trim()
}

/**
 * **Çoklu kullanıcı seçicisi** — sunucudan arayıp birden fazla kişi seçer (çip listesi).
 *
 * <p>Tekil seçici `ProxyUserPicker` (090) ile aynı davranış ailesinden ama iki noktada ayrılır:
 * seçim **çoklu**dur ve arama işlevi **prop olarak** gelir. Ortak tuzaklar buraya da taşındı:
 *
 * <ul>
 *   <li><b>Her aramada yeni referanslı dizi yazılır.</b> PrimeReact AutoComplete panelini yalnız
 *       `suggestions` referansı değiştiğinde açar; aynı terim tekrar arandığında referans
 *       değişmezse panel hiç açılmaz (039'da belgelenen tuzak).
 *   <li><b>Sıra koruması (seq guard).</b> Yavaş biten eski bir isteğin yanıtı, yeni terimin
 *       sonucunun üstüne yazmamalı — kullanıcı yazmaya devam ettiğinde liste geri sıçrardı.
 * </ul>
 *
 * <p><b>Sonuç önbelleğe alınMAZ</b> (tekil seçicideki `fetchQuery` + `staleTime` kalıbı yok):
 * arama sonucu bağlama bağlıdır (ör. zaten ekli kişiler sunucuda elenir), yani aynı terim farklı
 * bağlamda farklı sonuç verir. Ortak bir anahtar altında önbelleğe almak, bir bağlamın sonucunu
 * ötekine sızdırırdı; anahtarı bağlamla genişletmek ise önbelleğin faydasını neredeyse sıfırlar.
 *
 * <p>Seçilmiş bir kişi arama sonucunda yine görünebilir; bu bileşen onu <b>eler</b> — aynı kişiyi
 * ikinci kez çip olarak eklemek sunucunun reddedeceği bir istek üretirdi.
 *
 * <p><b>Jenerik DEĞİL, {@link PickableUser} üzerinden çalışır.</b> PrimeReact AutoComplete'in
 * jenerik imzası çoklu modda koşullu tip kullanır ({@code T extends any[] ? T[number] : T}) ve
 * kendi tip değişkenimizle çözülemez; bileşeni jenerik yapmak yalnızca her çağrı yerine tip
 * dönüşümü (cast) eklemek anlamına gelirdi. Çağıran, kendi daha geniş tipini doğrudan geçebilir —
 * yapısal uyum yeterli.
 */
export default function UserMultiPicker({
  inputId,
  value,
  onChange,
  search,
  placeholder,
  disabled,
  autoFocus,
  invalid,
  describedBy,
}: Readonly<Props>) {
  const [suggestions, setSuggestions] = useState<PickableUser[]>([])
  const seqRef = useRef(0)

  const complete = async (e: AutoCompleteCompleteEvent) => {
    const q = (e.query ?? '').trim()
    const seq = ++seqRef.current
    if (q.length < USER_PICKER_MIN_CHARS) {
      setSuggestions([]) // taze boş → panel kapanır
      return
    }
    try {
      const data = await search(q)
      if (seq !== seqRef.current) return
      const selected = new Set(value.map((u) => u.id))
      setSuggestions(data.filter((u) => !selected.has(u.id)))
    } catch {
      if (seq === seqRef.current) setSuggestions([])
    }
  }

  /**
   * Çoklu modda `e.value` seçili **nesnelerin** dizisidir; kullanıcı serbest metin yazdığında
   * PrimeReact bunu diziye eklemez, dolayısıyla süzgeç yalnız güvenlik ağıdır.
   */
  const onSelectionChange = (e: AutoCompleteChangeEvent) => {
    const next = Array.isArray(e.value) ? (e.value as unknown[]) : []
    onChange(next.filter((v): v is PickableUser => typeof v === 'object' && v !== null && 'id' in v))
  }

  /**
   * ARIA nitelikleri KÖKE değil İÇTEKİ GİRDİYE yazılmalı — `inputId` javadoc'undaki aynı sebep:
   * PrimeReact çoklu modda girdiyi bir `ul` içine gizler ve bileşene verilen prop'u köke koyar.
   * Passthrough (`pt`) bunun için var; doğru bölüm `input` (`data-pc-section="input"`).
   *
   * <p><b>Dönüşüm (cast) neden var — ve tipin neden "yanlış" olmadığı:</b> biçim AutoComplete'in
   * MODUNA bağlı, 25.09.2026'da ikisi de ölçüldü. <b>Tekil</b> modda girdi iç içe bir
   * {@code InputText}'in kökü olarak çizilir, yani doğru hedef {@code input.root}'tur ve tip
   * ({@code InputTextPassThroughOptions}) tam olarak onu bekler — orada dönüşüm gerekmez
   * (emsal: {@code ParticipantsSection}). <b>Çoklu</b> modda ise prop'lar DOĞRUDAN
   * {@code <input>}'a yazılır ({@code data-pc-section="input"}); {@code root} altına konursa
   * DOM'a {@code root="[object Object]"} diye bir öznitelik çıkar ve aria hiç yazılmaz — tip
   * derlenir, test kırılır. Dönüşüm yalnız bu mod farkını geçmek içindir.
   */
  const ariaPt = {
    input: {
      'aria-invalid': invalid ? 'true' : 'false',
      'aria-describedby': describedBy,
    },
  } as unknown as AutoCompletePassThroughOptions

  return (
    <AutoComplete
      multiple
      value={value}
      suggestions={suggestions}
      completeMethod={complete}
      delay={400}
      minLength={USER_PICKER_MIN_CHARS}
      inputId={inputId}
      placeholder={value.length === 0 ? placeholder : undefined}
      disabled={disabled}
      autoFocus={autoFocus}
      onChange={onSelectionChange}
      className="ui-user-picker"
      panelClassName="ui-user-picker-panel"
      /* ARIA nitelikleri KÖKE değil İÇTEKİ GİRDİYE yazılmalı — `inputId` javadoc'undaki
         aynı sebep: PrimeReact çoklu modda girdiyi bir `ul` içine gizler ve bileşene verilen
         prop'u köke koyar. Passthrough (`pt`) bunun için var. */
      pt={ariaPt}
      selectedItemTemplate={(item: PickableUser) => <span>{fullName(item)}</span>}
      itemTemplate={(item: PickableUser) => (
        <div className="ui-user-picker-item">
          <span className="ui-user-picker-name">{fullName(item)}</span>
          {item.employeeId && (
            <span className="ui-user-picker-sicil">{item.employeeId}</span>
          )}
        </div>
      )}
    />
  )
}
